import { useState, useRef, useEffect } from "react";

export default function useAudio() {
  const [audioPermission, setAudioPermission] = useState(false);
  const [hintActive, setHintActive] = useState(false);
  const [isClarificationPlaying, setIsClarificationPlaying] = useState(false);
  const [audioError, setAudioError] = useState(null);
  const hintAudioRef = useRef(null);
  
  // Request audio permission
  useEffect(() => {
    const requestAudioPermission = async () => {
      try {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        gainNode.gain.value = 0.001; // Nearly silent
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        oscillator.start(0);
        oscillator.stop(0.001);
        
        setAudioPermission(true);
      } catch (error) {
        console.log("Audio permission not yet granted");
      }
    };

    requestAudioPermission();

    const handleInteraction = () => {
      if (!audioPermission) {
        requestAudioPermission();
      }
    };

    document.addEventListener('click', handleInteraction);
    document.addEventListener('touchstart', handleInteraction);
    document.addEventListener('keydown', handleInteraction);

    return () => {
      document.removeEventListener('click', handleInteraction);
      document.removeEventListener('touchstart', handleInteraction);
      document.removeEventListener('keydown', handleInteraction);
    };
  }, [audioPermission]);

  const playAudio = (audioUrl) => {
    // Reset error state
    setAudioError(null);
    
    // Check if the audio URL is valid
    if (!audioUrl) {
      console.warn("No audio URL provided");
      setHintActive(false);
      setIsClarificationPlaying(false);
      return;
    }

    // Clean up any existing audio
    if (hintAudioRef.current) {
      hintAudioRef.current.pause();
      hintAudioRef.current.onended = null;
      hintAudioRef.current.onerror = null;
    }
    
    // Create a new audio element
    const audio = new Audio();
    
    // Set up event handlers before setting the source
    audio.onloadeddata = () => {
      // Audio loaded successfully
    };
    
    audio.oncanplay = () => {
      // Audio can play now
    };
    
    audio.onerror = (e) => {
      const error = e.target.error;
      console.error("Audio error:", error?.message || "Unknown error");
      setAudioError(`Error loading audio: ${error?.message || 'Unknown error'}`);
      setHintActive(false);
      setIsClarificationPlaying(false);
    };
    
    audio.onended = () => {
      setHintActive(false);
      setIsClarificationPlaying(false);
    };
    
    // If the URL is already a data URL format, use it directly
    // Otherwise try to create a data URL (assuming it's base64)
    let audioSrc = audioUrl;
    if (!audioUrl.startsWith('data:') && !audioUrl.startsWith('http')) {
      try {
        // Try to detect if it's already base64 encoded
        if (/^[A-Za-z0-9+/=]+$/.test(audioUrl.trim())) {
          audioSrc = `data:audio/mp3;base64,${audioUrl.trim()}`;
        }
      } catch (err) {
        console.error("Error converting audio URL:", err);
      }
    }
    
    // Assign the URL to the source
    audio.src = audioSrc;
    
    // Store the audio element in the ref
    hintAudioRef.current = audio;
    
    // Set state to indicate audio is playing
    setIsClarificationPlaying(true);
    setHintActive(true);
    
    // Attempt to play the audio with better error handling
    audio.play()
      .then(() => {
        // Audio playback started successfully
      })
      .catch(error => {
        console.error("Audio playback failed:", error.message);
        
        // Provide descriptive error based on the type
        let errorMessage;
        if (error.name === "NotSupportedError") {
          errorMessage = "The audio format is not supported by your browser";
        } else if (error.name === "NotAllowedError") {
          errorMessage = "Audio playback was prevented by your browser settings";
        } else {
          errorMessage = error.message || "Unknown audio playback error";
        }
        
        setAudioError(errorMessage);
        setHintActive(false);
        setIsClarificationPlaying(false);
      });
  };

  const stopAudio = () => {
    if (hintAudioRef.current) {
      hintAudioRef.current.pause();
      hintAudioRef.current.currentTime = 0;
      hintAudioRef.current.onended = null;
      hintAudioRef.current.onerror = null;
      hintAudioRef.current = null;
    }
    
    setHintActive(false);
    setIsClarificationPlaying(false);
    setAudioError(null);
  };
  
  return {
    audioPermission,
    hintActive,
    setHintActive,
    isClarificationPlaying, 
    setIsClarificationPlaying,
    playAudio,
    stopAudio,
    hintAudioRef,
    audioError
  };
}